/**
 * Per-device "last opened" stamps for chat threads.
 *
 * The homeowner site's `lib/threadRead.ts`, kept in step with it: a thread is
 * unread when its last message is newer than the last time THIS device opened
 * it. There is no server-side read receipt for this — it is a local
 * convenience, so a private window or blocked site data must degrade to
 * "nothing is ever marked read", never to a crash.
 *
 * ⚠️ KEYED BY UID: localStorage is shared by every account that uses the
 * browser, and a contractor and a homeowner do sign in on the same laptop.
 */

const MAX_ENTRIES = 200;

type ReadMap = Record<string, number>;

const keyFor = (uid: string) => `shoku.threadRead.${uid}`;

function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readMap(uid: string): ReadMap {
  const store = safeStorage();
  if (!store || !uid) return {};
  try {
    const raw = store.getItem(keyFor(uid));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as ReadMap;
  } catch {
    return {};
  }
}

function writeMap(uid: string, map: ReadMap): void {
  const store = safeStorage();
  if (!store || !uid) return;
  try {
    store.setItem(keyFor(uid), JSON.stringify(map));
  } catch {
    /* Quota or private mode — unread treatment just never persists. */
  }
}

export function lastOpened(uid: string, conversationId: string): number | null {
  if (!uid || !conversationId) return null;
  const at = readMap(uid)[conversationId];
  return typeof at === "number" ? at : null;
}

export function markOpened(uid: string, conversationId: string, at: number): void {
  if (!uid || !conversationId) return;
  const map = readMap(uid);
  map[conversationId] = at;

  const entries = Object.entries(map);
  if (entries.length > MAX_ENTRIES) {
    // Oldest stamp first, so trimming drops the threads least recently opened.
    entries.sort((a, b) => a[1] - b[1]);
    writeMap(uid, Object.fromEntries(entries.slice(entries.length - MAX_ENTRIES)));
    return;
  }
  writeMap(uid, map);
}
